import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { List, Card, Divider, Spin, Button, message, Alert, Popconfirm } from 'antd';
import { MobileOutlined, WifiOutlined, CloseOutlined } from '@ant-design/icons';
import {
  LeftPanel,
  RoomListWrapper,
  RightPanel,
  Title,
  IpAddress,
  InstructionsCard,
  InstructionStep,
  StepNumber,
  StepText,
  ConnectionCard,
} from './styles';
import WaitingForConnection from './WaitingForConnection';
import RoomItem from './RoomItem';
import NavigationBar from '@components/navigation-bar';
import { useGetExplorerCurrentIp } from '@src/emp-3.0/api/queryHooks';
import { useConnectionData } from '@src/pages/VisualActivation/store';
import { SOCKET_EVENTS } from '@src/pages/VisualActivation/constant';
import { getDeviceInfo } from '../utils';
import { useInitialInfoStore } from '@src/emp-3.0/store/useInitialInfo';

const RoomList = ({
  connected,
  error,
  sendMessage,
}: {
  connected: boolean;
  error?: Error;
  sendMessage: (message: string, data?: any, skipAck?: boolean) => Promise<void>;
}) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [openId, setOpenId] = useState<string | null>(null);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const { roomList } = useConnectionData();
  const { data: curIpv4, isFetching: ipv4ListLoading } = useGetExplorerCurrentIp(null, {
    enable: true,
  });
  const navigate = useNavigate();
  const { userInfo } = useInitialInfoStore();

  useEffect(() => {
    if (connected || error) {
      setLoading(false);
    }
    if (connected) {
      sendMessage(SOCKET_EVENTS.GET_ROOMS);
    }
  }, [error, connected, sendMessage]);

  const handleRoomClick = (roomId: string) => {
    navigate(`room?room_id=${encodeURIComponent(roomId)}`);
  };

  // sort rooms by can be connected
  const rooms = useMemo(() => {
    if (!roomList?.rooms) return [];

    return [...roomList.rooms].sort((a, b) => {
      const aCanConnect = a.ip === curIpv4?.value && !a.subscribers?.[0];
      const bCanConnect = b.ip === curIpv4?.value && !b.subscribers?.[0];

      // If a can connect and b cannot, a comes first
      if (aCanConnect && !bCanConnect) return -1;
      // If b can connect and a cannot, b comes first
      if (bCanConnect && !aCanConnect) return 1;
      // Otherwise maintain original order
      return 0;
    });
  }, [roomList, curIpv4]);

  return (
    <div style={{ background: '#fff', height: '100%' }}>
      <div style={{ background: '#fff' }}>
        <NavigationBar />
      </div>
      <RoomListWrapper>
        <LeftPanel>
          <InstructionsCard>
            <Title style={{ fontSize: 16, fontWeight: 600 }}>
              🚀 Engage Activation Mode on Your Device
            </Title>
            <InstructionStep>
              <StepNumber>1</StepNumber>
              <StepText>
                Ensure that both your device and computer are connected to the same Wi-Fi network,
                and make sure to open the VA tab first
              </StepText>
            </InstructionStep>
            <InstructionStep style={{ marginBottom: 0 }}>
              <StepNumber>2</StepNumber>
              <StepText>Open your app (with Conviva sensor integrated) on your device</StepText>
            </InstructionStep>
            <InstructionStep>
              <div style={{ marginLeft: '37px' }}>
                <span
                  style={{
                    background: '#ef4444',
                    color: 'white',
                    padding: '2px 8px',
                    borderRadius: 4,
                    fontSize: 12,
                    fontWeight: 'bold',
                    marginRight: 8,
                  }}
                >
                  REQUIRED
                </span>
                Only iOS, UIKit framework, and sensor versions greater than 1.2.3 are supported
              </div>
            </InstructionStep>
            <InstructionStep>
              <StepNumber>3</StepNumber>
              <StepText>
                Press and hold the screen with three fingers for more than 5 seconds to enter
                activation mode
              </StepText>
            </InstructionStep>
          </InstructionsCard>

          <ConnectionCard>
            <Title style={{ fontSize: 16, color: '#334155', marginBottom: 12 }}>
              <WifiOutlined style={{ marginRight: 8, color: '#3b82f6' }} />
              Network Information
            </Title>
            <Spin spinning={ipv4ListLoading}>
              <IpAddress $type={ipv4ListLoading ? 'inactive' : 'active'}>
                Your computer IP: <strong>{curIpv4?.value}</strong>
              </IpAddress>
            </Spin>
            <div style={{ color: '#64748b', fontSize: 14, fontStyle: 'italic', marginTop: 8 }}>
              * You can only connect to devices on the same network as your computer
            </div>
          </ConnectionCard>

          <div style={{ padding: '0 16px' }}>
            <Title>Connect Your Device</Title>
            <div>
              All engaged devices will be listed below. Please select your device to connect:
            </div>
          </div>
          {error && (
            <Alert description={error.message} type="error" showIcon style={{ margin: '16px 0' }} />
          )}
          <List
            loading={loading || ipv4ListLoading}
            dataSource={rooms}
            split={false}
            renderItem={(room) => {
              const { deviceName, osVersion } = getDeviceInfo(room.device_model);
              return (
                <List.Item>
                  <Card
                    style={{
                      width: '100%',
                      background: '#fff',
                      borderColor: '#e2e8f0',
                    }}
                  >
                    <List.Item.Meta
                      avatar={<MobileOutlined style={{ fontSize: 20 }} />}
                      title={<div>{`${deviceName} (${room.app_id})`}</div>}
                      description={
                        <RoomItem
                          type={'active'}
                          {...room}
                          osVersion={osVersion}
                          onConnect={handleRoomClick}
                        />
                      }
                    />
                    {userInfo?.role?.toLowerCase() === 'admin' && (
                      <Popconfirm
                        title="Remove Device"
                        open={openId === room.room_id}
                        okButtonProps={{ loading: confirmLoading }}
                        description="Are you sure to remove this device?"
                        onCancel={() => setOpenId(null)}
                        onConfirm={async () => {
                          try {
                            setConfirmLoading(true);
                            await sendMessage(
                              SOCKET_EVENTS.REMOVE_ROOM,
                              { room_id: room.room_id },
                              false,
                            );
                          } catch (error: any) {
                            message.error(error?.message || 'Failed to remove device');
                          } finally {
                            setConfirmLoading(false);
                            setOpenId(null);
                          }
                        }}
                        okText="Yes"
                        cancelText="No"
                      >
                        <Button
                          type="text"
                          style={{
                            position: 'absolute',
                            right: 5,
                            top: 5,
                            color: '#949494',
                          }}
                          icon={<CloseOutlined />}
                          onClick={() => setOpenId(room.room_id)}
                        />
                      </Popconfirm>
                    )}
                  </Card>
                </List.Item>
              );
            }}
          />
          {roomList?.conn_without_room && roomList.conn_without_room.length > 0 && (
            <>
              <Divider>
                Connection quota is full. Please remove the room and reactivate to proceed.
              </Divider>
              <List
                split={false}
                dataSource={roomList.conn_without_room}
                renderItem={(room) => {
                  const { deviceName, osVersion } = getDeviceInfo(room.device_model);
                  return (
                    <List.Item>
                      <Card style={{ width: '100%', borderColor: '#e2e8f0' }}>
                        <List.Item.Meta
                          avatar={<MobileOutlined style={{ fontSize: 20 }} />}
                          title={<div>{`${deviceName} (${room.app_id})`}</div>}
                          description={<RoomItem type="waiting" {...room} osVersion={osVersion} />}
                        />
                      </Card>
                    </List.Item>
                  );
                }}
              />
            </>
          )}
        </LeftPanel>
        <RightPanel>
          <WaitingForConnection />
          <Title style={{ marginTop: 24, color: '#334155', textAlign: 'center' }}>
            Screen Mirroring
          </Title>
        </RightPanel>
      </RoomListWrapper>
    </div>
  );
};

export default RoomList;
